import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Heading, Field, SubmitButton, ErrorNote, DoneBadge, PasswordHint } from "@/components/AuthUI";
import { getPasswordValidationError } from "@/lib/password";

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const passwordError = getPasswordValidationError(password);
    if (passwordError) return setError(passwordError);
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell homeTestId="reset-home-link" aside={false}>
      {!token ? (
        <div data-testid="reset-no-token" className="flex flex-col gap-4">
          <Heading title="Invalid link" sub="This reset link is missing or malformed. Please request a new one." />
          <Link to="/forgot-password" data-testid="request-new-link" className="text-[15px] font-semibold underline">
            Request a new link
          </Link>
        </div>
      ) : done ? (
        <div data-testid="reset-done" className="flex flex-col gap-4">
          <DoneBadge />
          <Heading title="Password reset" sub="Redirecting you to sign in…" />
        </div>
      ) : (
        <>
          <Heading title="Choose a new password" sub="Pick a strong password you will remember." />
          <form onSubmit={submit} className="flex flex-col gap-6">
            <Field
              label="New password"
              id="new-password"
              testid="reset-password-input"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={setPassword}
              required
            />
            <PasswordHint />
            <Field
              label="Confirm password"
              id="confirm-password"
              testid="reset-confirm-input"
              type="password"
              placeholder="Repeat your password"
              value={confirm}
              onChange={setConfirm}
              required
            />
            {error && <ErrorNote testid="reset-error">{error}</ErrorNote>}
            <SubmitButton testid="reset-submit-button" loading={loading}>
              Reset password
            </SubmitButton>
          </form>
          <Link to="/login" data-testid="back-to-login-link" className="text-[15px] text-soft underline self-start">
            Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
};
